import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useSearchParams } from "react-router-dom";
import "../styles/floor-management.css";
import {
  getFloorsPaginated,
  deleteFloor,
  deleteFloorsBulk,
} from "../services/floorService";
import ConfirmModal from "../components/ui/ConfirmModal";
import { useAppContext } from "../context/AppContext";
import { toast } from "../utils/toast";
import { getActionDropdownDirection } from "../utils/dropdownPosition";

const FloorManagementPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const lastLoadKeyRef = useRef("");
  const { setActiveSection, setFloorFormMode, setFloorId, buildingId } = useAppContext();

  const [floors, setFloors] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedIds, setSelectedIds] = useState(new Set());
  const [searchInput, setSearchInput] = useState(
    () => searchParams.get("search") || "",
  );
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [page, setPage] = useState(() => {
    const p = Number(searchParams.get("page") || 1);
    return Number.isInteger(p) && p > 0 ? p : 1;
  });
  const [limit, setLimit] = useState(() => {
    const l = Number(searchParams.get("limit") || 10);
    return Number.isInteger(l) && l > 0 ? l : 10;
  });
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    limit: 10,
    totalPages: 1,
  });
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    id: null,
    name: "",
    isBulk: false,
  });
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [dropdownDirection, setDropdownDirection] = useState("down");

  const reload = useCallback(async () => {
    try {
      setLoading(true);
      const result = await getFloorsPaginated({
        page,
        limit,
        search: debouncedSearch,
      });
      setFloors(Array.isArray(result?.data) ? result.data : []);
      setPagination(
        result?.meta || {
          total: 0,
          page: 1,
          limit: Number(limit) || 10,
          totalPages: 1,
        },
      );
    } catch (e) {
      toast.error(e?.message || "Failed to load floors");
    } finally {
      setLoading(false);
    }
  }, [page, limit, debouncedSearch]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch((searchInput || "").trim());
      setPage(1);
    }, 450);
    return () => clearTimeout(handle);
  }, [searchInput]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    const pageStr = String(page);
    const limitStr = String(limit);
    const searchStr = String(searchInput || "").trim();

    if (params.get("page") !== pageStr) params.set("page", pageStr);
    if (params.get("limit") !== limitStr) params.set("limit", limitStr);
    if (searchStr) params.set("search", searchStr);
    else params.delete("search");

    const current = searchParams.toString();
    const next = params.toString();
    if (current !== next) {
      setSearchParams(params, { replace: true });
    }
  }, [page, limit, searchInput, searchParams, setSearchParams]);

  useEffect(() => {
    const key = `${page}:${limit}:${debouncedSearch}`;
    if (lastLoadKeyRef.current === key) return;
    lastLoadKeyRef.current = key;
    reload();
  }, [page, limit, debouncedSearch, reload]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (activeDropdown && !event.target.closest(".actions-menu-wrapper")) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeDropdown]);

  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") setActiveDropdown(null);
    };
    document.addEventListener("keydown", handleEsc);
    return () => document.removeEventListener("keydown", handleEsc);
  }, []);

  const filteredFloors = useMemo(() => floors || [], [floors]);

  const allSelected = useMemo(
    () =>
      filteredFloors.length > 0 && selectedIds.size === filteredFloors.length,
    [filteredFloors, selectedIds],
  );

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredFloors.map((f) => f.floor_id)));
    }
  };

  const toggleSelect = (id) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    setSelectedIds(next);
  };

  const clearSelection = () => {
    setSelectedIds(new Set());
  };

  const goToCreate = () => {
    setFloorFormMode("create");
    setFloorId(null);
    setActiveSection("floor-create");
  };

  const goToEdit = (id) => {
    if (!id) return;
    setFloorFormMode("edit");
    setFloorId(id);
    setActiveSection("floor-edit", { floorId: id });
  };

  /** The same page read-only: the floor's details and its design layout. */
  const goToView = (id) => {
    if (!id) return;
    setFloorFormMode("view");
    setFloorId(id);
    setActiveSection("floor-detail", { floorId: id });
  };

  const removeSingle = (id) => {
    const floor = floors.find((f) => f.floor_id === id);
    const floorName = floor?.name || "Floor";
    setConfirmDelete({
      open: true,
      id,
      name: floorName,
      isBulk: false,
    });
  };

  const confirmRemoveSingle = async () => {
    const { id, name: floorName } = confirmDelete;
    setConfirmDelete({ open: false, id: null, name: "", isBulk: false });

    if (!id) {
      toast.error("No floor selected for deletion");
      return;
    }

    try {
      await deleteFloor(id);
      const label = floorName || "Floor";
      toast.success(`"${label}" has been deleted successfully!`);
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(id);
        return next;
      });
      await reload();
    } catch (e) {
      toast.error(e?.message || "Failed to delete floor");
      await reload();
    }
  };

  const bulkDelete = () => {
    if (selectedIds.size === 0) return;
    const count = selectedIds.size;
    setConfirmDelete({
      open: true,
      id: null,
      name: "",
      isBulk: true,
      count,
    });
  };

  const confirmBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    const count = ids.length;
    setConfirmDelete({ open: false, id: null, name: "", isBulk: false });

    if (ids.length === 0) {
      toast.error("No floors selected for deletion");
      return;
    }

    try {
      await deleteFloorsBulk(ids);
      toast.success(
        `${count} floor${count === 1 ? "" : "s"} deleted successfully!`,
      );
      setSelectedIds(new Set());
      await reload();
    } catch (e) {
      toast.error(e?.message || "Failed to delete selected floors");
      await reload();
    }
  };

  const totalPages = Math.max(1, Number(pagination.totalPages) || 1);
  const currentPage = Math.min(
    Math.max(1, Number(pagination.page) || page),
    totalPages,
  );
  const totalRows = Number(pagination.total) || 0;
  const pageSize = Number(pagination.limit) || limit;
  const showingFrom = totalRows === 0 ? 0 : (currentPage - 1) * pageSize + 1;
  const showingTo =
    totalRows === 0 ? 0 : Math.min(currentPage * pageSize, totalRows);

  const visiblePageItems = useMemo(() => {
    if (totalPages <= 7) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (currentPage <= 2) {
      return [1, 2, 3, "ellipsis", totalPages];
    }

    if (currentPage === 3) {
      return [1, 2, 3, 4, "ellipsis", totalPages];
    }

    if (currentPage === totalPages - 2) {
      return [
        1,
        "ellipsis",
        totalPages - 3,
        totalPages - 2,
        totalPages - 1,
        totalPages,
      ];
    }

    if (currentPage >= totalPages - 1) {
      return [1, "ellipsis", totalPages - 2, totalPages - 1, totalPages];
    }

    return [
      1,
      "ellipsis",
      currentPage - 1,
      currentPage,
      currentPage + 1,
      "ellipsis",
      totalPages,
    ];
  }, [totalPages, currentPage]);

  const limitOptions = useMemo(() => {
    const base = [5, 10, 20, 50];
    const current = Number(limit);
    const all =
      Number.isInteger(current) && current > 0 ? [...base, current] : base;
    return Array.from(new Set(all)).sort((a, b) => a - b);
  }, [limit]);

  return (
    <div className="page-container floor-page">
      <div className="page-header">
        <div className="header-content">
          <div>
            <h2 className="page-title">Floor Management</h2>
            <p className="page-subtitle">
              Manage floors across all buildings .
            </p>
          </div>
          <div className="roles-toolbar">
            <div className="roles-search">
              <input
                className="search-input"
                type="text"
                placeholder="Search by code, name, building, company"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
              <button
                className="secondary-btn sm"
                onClick={() => setSearchInput("")}
                disabled={loading}
              >
                Clear
              </button>
            </div>
            <button
              className="secondary-btn"
              onClick={() => setActiveSection("building-layout")}
              disabled={!buildingId}
              title={
                buildingId
                  ? undefined
                  : "Open a building first to design its floor layouts"
              }
            >
              Design layout
            </button>
            <button className="primary-btn" onClick={goToCreate}>
              + Add Floor
            </button>
          </div>
        </div>
      </div>

      <div className="page-content">
        <div className="floor-pagination-top">
          <label htmlFor="floor-limit" className="floor-pagination-label">
            Rows per page:
          </label>
          <select
            id="floor-limit"
            value={limit}
            onChange={(e) => {
              const next = Number(e.target.value) || 10;
              setLimit(next);
              setPage(1);
            }}
          >
            {limitOptions.map((opt) => (
              <option key={opt} value={opt}>
                {opt}
              </option>
            ))}
          </select>
        </div>

        {selectedIds.size > 0 && (
          <div
            style={{
              marginBottom: "1rem",
              padding: "1rem 1.25rem",
              background: "var(--accent-tint-50)",
              borderRadius: "0.75rem",
              border: "1px solid var(--accent-tint-200)",
              boxShadow: "0 1px 2px rgba(0, 0, 0, 0.05)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: "0.75rem",
              }}
            >
              <span
                style={{
                  fontWeight: "600",
                  color: "var(--accent-ink-800)",
                  fontSize: "0.9375rem",
                }}
              >
                {selectedIds.size} {selectedIds.size === 1 ? "floor" : "floors"}{" "}
                selected
              </span>
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
                <button className="danger-btn sm" onClick={bulkDelete}>
                  Delete Selected
                </button>
                <button className="secondary-btn sm" onClick={clearSelection}>
                  Clear Selection
                </button>
              </div>
            </div>
          </div>
        )}

        {loading ?
          <div className="no-results">Loading floors...</div>
        : <div className="roles-table">
            <div className="roles-table-header">
              <div className="cell checkbox">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={toggleSelectAll}
                />
              </div>
              <div className="cell">Code</div>
              <div className="cell">Name</div>
              <div className="cell">Building</div>
              <div className="cell">Company</div>
              <div className="cell actions">Actions</div>
            </div>

            {filteredFloors.length === 0 ?
              <div className="no-results">No floors found.</div>
            : filteredFloors.map((f) => (
                <div
                  key={f.floor_id}
                  className={`roles-table-row ${
                    selectedIds.has(f.floor_id) ? "selected" : ""
                  }`}
                >
                  <div className="cell checkbox">
                    <input
                      type="checkbox"
                      checked={selectedIds.has(f.floor_id)}
                      onChange={() => toggleSelect(f.floor_id)}
                    />
                  </div>
                  <div className="cell">{f.floor_code}</div>
                  <div className="cell">{f.name}</div>
                  <div className="cell">
                    {f?.building?.name || `Building ${f.building_id || "-"}`}
                  </div>
                  <div className="cell">
                    {f?.building?.company?.name || "-"}
                  </div>
                  <div className="cell actions">
                    <div
                      className="actions-menu-wrapper"
                      style={{ position: "relative", display: "inline-block" }}
                    >
                      <button
                        className="action-menu-btn"
                        aria-haspopup="true"
                        aria-expanded={activeDropdown === f.floor_id}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (activeDropdown === f.floor_id) {
                            setActiveDropdown(null);
                          } else {
                            setDropdownDirection(
                              getActionDropdownDirection(e.currentTarget),
                            );
                            setActiveDropdown(f.floor_id);
                          }
                        }}
                      >
                        Actions ▼
                      </button>
                      {activeDropdown === f.floor_id && (
                        <div
                          className={`action-dropdown-menu${
                            dropdownDirection === "up" ? " dropdown-up" : ""
                          }`}
                          role="menu"
                        >
                          <button
                            className="action-menu-item"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              goToView(f.floor_id);
                            }}
                          >
                            View
                          </button>
                          <button
                            className="action-menu-item"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              goToEdit(f.floor_id);
                            }}
                          >
                            Edit
                          </button>
                          <button
                            className="action-menu-item danger"
                            role="menuitem"
                            onClick={(e) => {
                              e.stopPropagation();
                              setActiveDropdown(null);
                              removeSingle(f.floor_id);
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            }
          </div>
        }

        <div className="floor-pagination-bottom">
          <div className="floor-pagination-summary">
            Showing {showingFrom} to {showingTo} of {totalRows} entries
          </div>
          <div className="floor-pagination-actions">
            <button
              className="secondary-btn sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage <= 1 || loading}
            >
              Previous
            </button>
            {visiblePageItems.map((item, index) => {
              if (item === "ellipsis") {
                return (
                  <span
                    key={`ellipsis-${index}`}
                    className="secondary-btn sm"
                    aria-hidden="true"
                  >
                    ...
                  </span>
                );
              }
              const p = Number(item);
              return (
                <button
                  key={p}
                  className={
                    p === currentPage ? "primary-btn sm" : "secondary-btn sm"
                  }
                  onClick={() => setPage(p)}
                  disabled={loading}
                >
                  {p}
                </button>
              );
            })}
            <button
              className="secondary-btn sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage >= totalPages || loading}
            >
              Next
            </button>
          </div>
        </div>

        <ConfirmModal
          open={confirmDelete.open}
          title="Confirm Delete"
          message={
            confirmDelete.isBulk ?
              `Are you sure you want to delete ${confirmDelete.count} floor(s)? This action cannot be undone.`
            : `Are you sure you want to delete floor "${confirmDelete.name}"? This action cannot be undone.`
          }
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={
            confirmDelete.isBulk ? confirmBulkDelete : confirmRemoveSingle
          }
          onCancel={() =>
            setConfirmDelete({ open: false, id: null, name: "", isBulk: false })
          }
          isDanger={true}
        />
      </div>
    </div>
  );
};

export default FloorManagementPage;
